/**
 * Stage 3 — the authored test scripts, and GATE 2 (approve & assign).
 *
 * Each script shows its steps, test data, and — for automatic tests — the
 * VQL assertions the executor will run. While the run is `authored` the lead
 * can flip a test's mode and assign a tester; approving requires every manual
 * test to have one.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCheck, IconInfoCircle, IconRobot, IconUser } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { listUsers, type UserOut } from "@/shared/api/users";
import {
  approveTests,
  updateTest,
  type RunOut,
  type TestMode,
  type TestOut,
} from "@/shared/api/validation";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { TEST_STATUS_COLOR, TEST_STATUS_LABEL, riskColor } from "../stageMeta";
import { AdvisoryNote, Provenance } from "./Provenance";

interface Props {
  run: RunOut;
  onUpdated: (run: RunOut, jump?: boolean) => void;
}

export function TestsStage({ run, onUpdated }: Props) {
  const getToken = useAccessToken();
  const editable = run.is_owner && run.status === "authored";
  const assignable = run.is_owner && ["authored", "executing"].includes(run.status);
  const [users, setUsers] = useState<UserOut[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    if (!assignable) return;
    try {
      const page = await listUsers(await getToken(), { limit: 200, status: "active" });
      setUsers(page.items);
    } catch (err) {
      toastApiError("Could not list users for assignment", err);
    }
  }, [getToken, assignable]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.user_id,
        label: u.display_name ? `${u.display_name} (${u.email})` : u.email || u.user_id,
      })),
    [users],
  );

  const patch = async (test: TestOut, changes: Parameters<typeof updateTest>[3], label: string) => {
    setBusy(test.test_id);
    try {
      onUpdated(await updateTest(await getToken(), run.run_id, test.test_id, changes), false);
      toastSuccess(label, test.code);
    } catch (err) {
      toastApiError(`Could not update ${test.code}`, err);
    } finally {
      setBusy(null);
    }
  };

  const approve = async () => {
    setBusy("approve");
    try {
      const fresh = await approveTests(await getToken(), run.run_id);
      onUpdated(fresh);
      toastSuccess("Tests approved", "Execution is open — testers can start.");
    } catch (err) {
      toastApiError("Could not approve tests", err);
    } finally {
      setBusy(null);
    }
  };

  if (run.tests.length === 0) {
    return (
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />}>
        No tests yet. Author them from the Validation plan tab once the plan is in place.
      </Alert>
    );
  }

  const manual = run.tests.filter((t) => t.mode === "manual");
  const automatic = run.tests.filter((t) => t.mode === "automatic");
  const unassigned = manual.filter((t) => !t.assignee_user_id);
  const reused = run.tests.filter((t) => t.reused_from).length;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text fw={600}>
            {run.tests.length} test scripts · {automatic.length} automatic · {manual.length} manual
            {reused ? ` · ${reused} reused from existing cases` : ""}
          </Text>
          <Provenance
            promptVersion={run.tests_prompt_version}
            inferenceId={run.tests_inference_id}
          />
          {run.status === "authored" ? <AdvisoryNote /> : null}
        </Stack>
        {editable && (
          <Tooltip
            label={
              unassigned.length
                ? `Assign a tester to ${unassigned.map((t) => t.code).join(", ")} first`
                : "Human gate 2 — opens execution"
            }
          >
            <Button
              leftSection={<IconCheck size={16} />}
              disabled={unassigned.length > 0}
              loading={busy === "approve"}
              onClick={() => void approve()}
            >
              Approve tests & open execution
            </Button>
          </Tooltip>
        )}
      </Group>

      {editable && unassigned.length > 0 && (
        <Alert variant="light" color="yellow" icon={<IconInfoCircle size={16} />}>
          {unassigned.length} manual test{unassigned.length === 1 ? " needs" : "s need"} a tester
          before you can approve. Testers can be any user — they only ever see the tests assigned to
          them.
        </Alert>
      )}

      <Accordion variant="separated" multiple>
        {run.tests.map((t) => (
          <Accordion.Item key={t.test_id} value={t.test_id}>
            <Accordion.Control>
              <Group justify="space-between" wrap="nowrap" pr="md">
                <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Badge variant="outline" color="gray" size="sm">
                    {t.code}
                  </Badge>
                  <div style={{ minWidth: 0 }}>
                    <Text fw={600} size="sm" truncate>
                      {t.title}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      covers {t.risk_ref ?? "—"}
                      {t.requirement_ref ? ` · ${t.requirement_ref}` : ""} · {t.steps.length} steps
                      {t.reused_from ? ` · reused ${t.reused_from}` : ""}
                    </Text>
                  </div>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  {t.gxp_risk && (
                    <Badge size="sm" variant="light" color={riskColor(t.gxp_risk)}>
                      {t.gxp_risk}
                    </Badge>
                  )}
                  <Badge
                    size="sm"
                    variant="light"
                    color={t.mode === "automatic" ? "violet" : "blue"}
                    leftSection={
                      t.mode === "automatic" ? <IconRobot size={11} /> : <IconUser size={11} />
                    }
                  >
                    {t.mode}
                  </Badge>
                  <Badge size="sm" variant="light" color={TEST_STATUS_COLOR[t.status]}>
                    {TEST_STATUS_LABEL[t.status]}
                  </Badge>
                </Group>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                {t.objective && <Text size="sm">{t.objective}</Text>}
                {(editable || assignable) && (
                  <Paper withBorder radius="sm" p="sm" bg="var(--mantine-color-default-hover)">
                    <Group align="flex-end" gap="md">
                      {editable && (
                        <div>
                          <Text size="xs" fw={600} c="dimmed" mb={4}>
                            Execution mode
                          </Text>
                          <SegmentedControl
                            size="xs"
                            value={t.mode}
                            disabled={busy === t.test_id}
                            onChange={(v) =>
                              void patch(t, { mode: v as TestMode }, `Mode set to ${v}`)
                            }
                            data={[
                              { label: "Manual", value: "manual" },
                              {
                                label: "Automatic",
                                value: "automatic",
                                disabled: !run.veeva_connection_id,
                              },
                            ]}
                          />
                        </div>
                      )}
                      {t.mode === "manual" && (
                        <Select
                          size="xs"
                          label="Assigned tester"
                          placeholder="Pick a user"
                          data={userOptions}
                          value={t.assignee_user_id}
                          onChange={(v) =>
                            void patch(
                              t,
                              { assignee_user_id: v ?? "" },
                              v ? "Tester assigned" : "Tester unassigned",
                            )
                          }
                          searchable
                          clearable
                          disabled={busy === t.test_id || !assignable}
                          style={{ minWidth: 320 }}
                        />
                      )}
                      {t.mode_rationale && (
                        <Text size="xs" c="dimmed" style={{ flex: 1 }}>
                          {t.mode_rationale}
                        </Text>
                      )}
                    </Group>
                  </Paper>
                )}
                {!editable && t.mode === "manual" && !assignable && (
                  <Text size="xs" c="dimmed">
                    Tester: {t.assignee_display_name ?? t.assignee_email ?? "unassigned"}
                  </Text>
                )}
                {t.preconditions && (
                  <Text size="xs" c="dimmed">
                    <b>Preconditions:</b> {t.preconditions}
                  </Text>
                )}
                <Table withTableBorder verticalSpacing={4}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={40}>#</Table.Th>
                      <Table.Th>Action</Table.Th>
                      <Table.Th>Expected result</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {t.steps.map((s) => (
                      <Table.Tr key={s.n}>
                        <Table.Td>{s.n}</Table.Td>
                        <Table.Td>
                          <Text size="sm">{s.action}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">
                            {s.expected}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                {t.test_data.length > 0 && (
                  <Text size="xs" c="dimmed">
                    <b>Test data:</b>{" "}
                    {t.test_data
                      .map((d) => `${d.item}${d.value ? ` = ${d.value}` : ""}`)
                      .join("; ")}
                  </Text>
                )}
                {t.mode === "automatic" && t.api_assertions.length > 0 && (
                  <Paper withBorder radius="sm" p="sm">
                    <Text size="xs" fw={600} c="dimmed" mb={4}>
                      API assertions (read-only VQL)
                    </Text>
                    <Stack gap={4}>
                      {t.api_assertions.map((a, i) => (
                        <div key={i}>
                          <Text size="xs">{a.description}</Text>
                          <Code block>{a.vql}</Code>
                          <Text size="xs" c="dimmed">
                            expect {a.expect.kind}
                            {a.expect.field ? ` · ${a.expect.field}` : ""}
                            {a.expect.value !== undefined && a.expect.value !== null
                              ? ` = ${String(a.expect.value)}`
                              : ""}
                          </Text>
                        </div>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Stack>
  );
}
